/**
 * Birth-date numerology arithmetic: the one TypeScript home of the digit
 * reduction the engine runs (apps/backend/app/engine/calculators/numerology.py).
 *
 *   reduceDigits(n)    = _reduce_to_single_digit: re-sum the digits until 1-9.
 *   mulankOf(day)      = calculate_mulank: reduce the birth day.
 *   bhagyankOf(date)   = calculate_bhagyank: reduce every digit of DDMMYYYY.
 *
 * The engine reduces fully, so a total of 11, 22 or 33 ends at 2, 4 or 6.
 * `master` only REPORTS that the chain passed through one of them, for
 * /numerology/life-path/, whose own rule keeps master numbers.
 * numerology-digits.test.ts pins parity against the engine's calibration tables,
 * read from the backend tests themselves, so an engine change fails web CI too.
 *
 * Zero imports on purpose: the client date-check island imports this module,
 * so it must stay free of @ishvaram/domain-data and server-only code.
 */

export type BirthDate = { year: number; month: number; day: number };

type MasterNumber = 11 | 22 | 33;

export type Reduction = {
  /** The digits that were added, in display order (DD MM YYYY for a full date). */
  digits: number[];
  /** Their sum, then every re-sum, ending at the 1-9 value: 29 -> [29, 11, 2]. */
  chain: number[];
  /** The engine's single-digit result. */
  value: number;
};

export type BhagyankReduction = Reduction & {
  /** The first master number the chain reached, or null. */
  master: MasterNumber | null;
};

export type BirthDateProblem = "empty" | "invalid" | "too-early" | "future";

export type ParsedBirthDate =
  { ok: true; date: BirthDate } | { ok: false; problem: BirthDateProblem };

const MASTER_NUMBERS: readonly number[] = [11, 22, 33];
const EARLIEST_BIRTH_DATE: BirthDate = { year: 1900, month: 1, day: 1 };
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function digitsIn(text: string): number[] {
  return text.split("").map(Number);
}

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/** The decimal digits of a non-negative integer: 29 -> [2, 9]. */
export function digitsOf(n: number): number[] {
  return digitsIn(String(n));
}

/** The digit reduction that DEFINES mulank and bhagyank: the engine's. */
export function reduceDigits(n: number): number {
  let value = n;
  while (value > 9) value = sum(digitsOf(value));
  return value;
}

/**
 * Every value the reduction passes through: 29 -> [29, 11, 2]; 7 -> [7].
 * @internal
 */
export function reductionChain(total: number): number[] {
  const chain = [total];
  let value = total;
  while (value > 9) {
    value = sum(digitsOf(value));
    chain.push(value);
  }
  return chain;
}

function reduce(digits: number[]): Reduction {
  const chain = reductionChain(sum(digits));
  return { digits, chain, value: chain[chain.length - 1] };
}

function firstMaster(chain: readonly number[]): MasterNumber | null {
  const master = chain.find((value) => MASTER_NUMBERS.includes(value));
  return master === undefined ? null : (master as MasterNumber);
}

/** Mulank: the birth day alone, reduced to 1-9. */
export function mulankOf(day: number): Reduction {
  return reduce(digitsOf(day));
}

/** Bhagyank: every digit of DD, MM and YYYY (in that order), reduced to 1-9. */
export function bhagyankOf(date: BirthDate): BhagyankReduction {
  const pad = (value: number) => String(value).padStart(2, "0");
  const reduction = reduce(digitsIn(`${pad(date.day)}${pad(date.month)}${date.year}`));
  return { ...reduction, master: firstMaster(reduction.chain) };
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  return month === 2 && isLeapYear(year) ? 29 : MONTH_DAYS[month - 1];
}

/** YYYY-MM-DD that names a real calendar day, or null. */
function calendarDate(text: string): BirthDate | null {
  const match = ISO_DATE.exec(text);
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function sortKey(date: BirthDate): number {
  return date.year * 10000 + date.month * 100 + date.day;
}

function localDate(now: Date): BirthDate {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/**
 * Validates a date-input value (YYYY-MM-DD) as a birth date between 1900-01-01
 * and the visitor's own today. Call it from an event handler, never during
 * render: `today` defaults to the client clock.
 */
export function parseBirthDate(input: string, today: Date = new Date()): ParsedBirthDate {
  const text = input.trim();
  if (!text) return { ok: false, problem: "empty" };
  const date = calendarDate(text);
  if (!date) return { ok: false, problem: "invalid" };
  if (sortKey(date) < sortKey(EARLIEST_BIRTH_DATE)) return { ok: false, problem: "too-early" };
  if (sortKey(date) > sortKey(localDate(today))) return { ok: false, problem: "future" };
  return { ok: true, date };
}
